import { Button, Checkbox, Form, Input, InputNumber, Space, Table, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { useModal } from "../../core/context/ModalContext";
import { CopyOutlined, EyeOutlined, MailOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import { useUser } from "../../core/context/UserContext";
import { writeClipboard } from "../../core/utils/clipboard-utils";

export default function StudyShareModal({ studyIds = [] }) {
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const [form] = Form.useForm();
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [shareLinks, setShareLinks] = useState([]);
    const [viewerLoading, setViewerLoading] = useState(false);
    const [emailMode, setEmailMode] = useState(false);

    useEffect(() => {
        form.setFieldsValue({
            validityHours: 72,
            description: '',
            includeViewer: true,
            includeReports: true
        });
    }, []);

    const createLinks = async () => {
        const values = await form.getFieldsValue();

        const response = await ApiService.StudyShare.CreateShareLinks({
            studyIdList: studyIds,
            validityHours: values.validityHours,
            description: values.description || null,
            includeViewer: values.includeViewer,
            includeReports: values.includeReports
        });

        if (!response.success) {
            return null;
        }

        setShareLinks(response.data);
        return response.data;
    };

    const handleCreateLinks = async () => {
        try {
            setLoading(true);
            await createLinks();
        } catch (error) {
            console.error('Form validation error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendEmail = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const emailAddresses = values.emailAddresses
                .split(',')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            const response = await ApiService.StudyShare.SendShareLinksByEmail({
                studyIds: studyIds,
                validityHours: values.validityHours,
                emailAddresses: emailAddresses,
                description: values.description || null,
                includeViewer: values.includeViewer,
                includeReports: values.includeReports
            });

            if (response.success) {
                toast.success(t('Emails sent successfully'));
                closeModal();
            }
        } catch (error) {
            console.error('Form validation error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Güvenli olmayan bağlam / izin reddi / jest kopması durumlarının tamamı writeClipboard içinde
    // ele alınıyor (core/utils/clipboard-utils.js); burada sadece sonucu kullanıcıya bildiriyoruz.
    const copyLinkToClipboard = async (text) => {
        if (await writeClipboard(text)) {
            toast.success(t('Link copied to clipboard'));
        } else {
            toast.error(t('Could not copy the link'));
        }
    };

    const buildLinkText = (links, urlOf) => {
        return links.map(link => [
            link.patientName || '',
            link.modality || '',
            link.studyDescription || '',
            link.accessionNumber || '',
            urlOf(link)
        ].join('\n')).join('\n\n---\n\n');
    };

    const copyAllLinks = async () => {
        const fullText = buildLinkText(shareLinks, link => link.downloadUrl);

        if (await writeClipboard(fullText)) {
            toast.success(t('All links copied to clipboard'));
        } else {
            toast.error(t('Could not copy the link'));
        }
    };

    // Görüntüleyici linki: indirme yerine tarayıcıda açan tek adres — seçilen çekimlerin hepsi aynı
    // linkten (sekmeli) açılıyor, o yüzden satır başına değil paylaşım başına bir adres var. Link
    // henüz oluşturulmadıysa burada oluşturuluyor; oluşturulmuşsa aynı paylaşım yeniden kullanılıyor
    // ki her tıklamada yeni bir paylaşım kaydı açılmasın.
    const copyViewerLink = async () => {
        try {
            setViewerLoading(true);

            const links = shareLinks.length > 0 ? shareLinks : await createLinks();
            if (!links || links.length === 0) {
                return;
            }

            if (await writeClipboard(links[0].viewerUrl)) {
                toast.success(t('Viewer link copied to clipboard'));
            } else {
                toast.error(t('Could not copy the link'));
            }
        } catch (error) {
            console.error('Viewer link error:', error);
        } finally {
            setViewerLoading(false);
        }
    };

    const columns = [
        {
            title: t('Study ID'),
            dataIndex: 'studyId',
            key: 'studyId'
        },
        {
            title: t('Patient Name'),
            dataIndex: 'patientName',
            key: 'patientName',
            ellipsis: true
        },
        {
            title: t('Accession Number'),
            dataIndex: 'accessionNumber',
            key: 'accessionNumber'
        },
        {
            title: t('Modality'),
            dataIndex: 'modality',
            key: 'modality'
        },
        {
            title: t('Study Description'),
            dataIndex: 'studyDescription',
            key: 'studyDescription',
            ellipsis: true
        },
        {
            title: t('Share Link'),
            dataIndex: 'downloadUrl',
            key: 'downloadUrl',
            render: (text) => (
                <Space>
                    <Input
                        value={text}
                        readOnly
                        style={{ minWidth: 200, maxWidth: 400 }}
                    />
                    <Tooltip title={t('Copy')}>
                        <Button
                            icon={<CopyOutlined />}
                            onClick={() => copyLinkToClipboard(text)}
                        />
                    </Tooltip>
                </Space>
            )
        },
        {
            title: t('Expiry Date'),
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: (date) => moment(date).format('DD.MM.YYYY HH:mm')
        }
    ];

    return (
        <div style={{ maxWidth: '100%', width: '100%' }}>
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t('Validity Duration (Hours)')}
                    name="validityHours"
                    rules={[
                        { required: true, message: t('Please enter validity duration') },
                        { type: 'number', min: 1, max: 720, message: t('Value must be between 1 and 720') }
                    ]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        max={720}
                        placeholder={t('Enter hours (1-720)')}
                    />
                </Form.Item>

                <Form.Item
                    label={t('Description')}
                    name="description"
                >
                    <Input.TextArea
                        rows={3}
                        maxLength={500}
                        placeholder={t('Optional description for the share link')}
                        showCount
                    />
                </Form.Item>

                <Form.Item
                    name="includeViewer"
                    valuePropName="checked"
                    style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
                >
                    <Checkbox>{t('Include Viewer')}</Checkbox>
                </Form.Item>
                <Form.Item
                    name="includeReports"
                    valuePropName="checked"
                    style={{ display: 'inline-block', width: 'calc(50% - 8px)', marginLeft: '16px' }}
                >
                    <Checkbox>{t('Include Reports')}</Checkbox>
                </Form.Item>

                {emailMode && (
                    <Form.Item
                        label={t('Email Addresses')}
                        name="emailAddresses"
                        rules={[
                            { required: true, message: t('Please enter at least one email address') },
                            {
                                pattern: /^[\w\-\.]+@([\w-]+\.)+[\w-]{2,}(,\s*[\w\-\.]+@([\w-]+\.)+[\w-]{2,})*$/,
                                message: t('Please enter valid email addresses separated by commas')
                            }
                        ]}
                    >
                        <Input.TextArea
                            rows={2}
                            placeholder={t('Enter email addresses separated by commas')}
                        />
                    </Form.Item>
                )}
            </Form>

            {shareLinks.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold' }}>{t('Share Links')} ({shareLinks.length})</span>
                        <Button
                            icon={<CopyOutlined />}
                            onClick={copyAllLinks}
                        >
                            {t('Copy All Links')}
                        </Button>
                    </div>
                    <Table
                        dataSource={shareLinks}
                        columns={columns}
                        rowKey="studyId"
                        pagination={false}
                        size="small"
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Tooltip title={t('A single link that opens the selected studies in the web viewer, without downloading')}>
                    <Button
                        icon={<EyeOutlined />}
                        loading={viewerLoading}
                        onClick={copyViewerLink}
                    >
                        {t('Copy Viewer Link')}
                    </Button>
                </Tooltip>
                <Space>
                    <Button onClick={closeModal}>
                        {t('Cancel')}
                    </Button>
                    {!emailMode ? (
                        <>
                            <Button
                                type="default"
                                icon={<MailOutlined />}
                                onClick={() => setEmailMode(true)}
                            >
                                {t('Send by Email')}
                            </Button>
                            <Button
                                type="primary"
                                loading={loading}
                                onClick={handleCreateLinks}
                            >
                                {t('Create Links')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => setEmailMode(false)}>
                                {t('Back')}
                            </Button>
                            <Button
                                type="primary"
                                icon={<MailOutlined />}
                                loading={loading}
                                onClick={handleSendEmail}
                            >
                                {t('Send Emails')}
                            </Button>
                        </>
                    )}
                </Space>
            </div>
        </div>
    );
}
